import { useState } from "react";
import { Box, Typography, Popover, Skeleton, Tooltip, IconButton } from "@mui/material";
import { EditNote } from "@mui/icons-material";
import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "../theme/useTheme.js";
import { Button } from "./Button.jsx";
import { useAuth } from "./contexts/AuthContext.jsx";
import apiClient from "../api-client.js";
import { formatRelativeDate } from "../utils/dateUtils";
import { isSupportTicketNotification, openSupportTicket } from "../utils/supportTickets";
import { NOTIFICATION_TYPES } from "../Pages/Dashboard/components/NotificationPanel";
import {
  NOTIFICATION_MENU_QUERY_KEY,
  getContentNotificationRoute,
  getNotificationRoute,
  getNotificationSummary,
  invalidateNotificationQueries,
  isContentNotification,
} from "../Pages/Dashboard/notificationRoutes";

const PREVIEW_SIZE = 20;

// Anuncios y tareas se abren en modales que solo existen en el dashboard
const DASHBOARD_ONLY_TYPES = ["central_announcement", "task_assigned"];

// clear-thread limpia todo el hilo, no solo la notificación pulsada
const isThreadNotification = (notification) =>
  notification.type === "unread_in_thread" && Boolean(notification.thread_id);

export const NotificationMenu = ({ notificationsCount = 0, onConversationNotificationClick }) => {
  const { theme, colors, neutrals, notificationColors } = useTheme();
  const { user, interceptorReady } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const isOpen = Boolean(anchorEl);

  const { data, isLoading, isError } = useQuery({
    queryKey: NOTIFICATION_MENU_QUERY_KEY,
    queryFn: () =>
      apiClient
        .get(`dashboard/notifications/?is_read=false&page_size=${PREVIEW_SIZE}`)
        .then((res) => res.data),
    enabled: isOpen && interceptorReady,
    staleTime: 0,
  });
  const notifications = data?.results ?? [];

  const removeFromMenu = (shouldRemove) =>
    queryClient.setQueryData(NOTIFICATION_MENU_QUERY_KEY, (old) =>
      old && { ...old, results: old.results.filter((notification) => !shouldRemove(notification)) }
    );

  const close = () => setAnchorEl(null);

  const goToDashboard = (openNotification = null) => {
    close();
    navigate("/dashboard", { state: { openNotification } });
  };

  const markAsRead = async (notification) => {
    if (isThreadNotification(notification)) {
      removeFromMenu((item) => item.thread_id === notification.thread_id && item.type === "unread_in_thread");
    } else {
      removeFromMenu((item) => item.id === notification.id);
    }

    try {
      if (isThreadNotification(notification)) {
        await apiClient.post("dashboard/notifications/clear-thread/", {
          thread_id: notification.thread_id,
        });
        onConversationNotificationClick?.();
      } else {
        await apiClient.post("dashboard/notifications/mark-read/", { ids: [notification.id] });
      }
    } catch (error) {
      console.error("Error al marcar la notificación como leída:", error);
    } finally {
      invalidateNotificationQueries(queryClient);
    }
  };

  const markAllAsRead = async () => {
    setIsMarkingAll(true);
    removeFromMenu(() => true);
    try {
      await apiClient.post("dashboard/notifications/mark-all-read/");
      onConversationNotificationClick?.();
    } catch (error) {
      console.error("Error al marcar todas las notificaciones:", error);
    } finally {
      invalidateNotificationQueries(queryClient);
      setIsMarkingAll(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (DASHBOARD_ONLY_TYPES.includes(notification.type)) {
      markAsRead(notification);
      goToDashboard(notification);
      return;
    }

    const destination = isContentNotification(notification)
      ? getContentNotificationRoute(notification, user?.role)
      : getNotificationRoute(notification, user?.role);
    if (!destination) {
      goToDashboard();
      return;
    }

    close();
    markAsRead(notification);

    if (isSupportTicketNotification(notification)) {
      openSupportTicket(notification.ticket_id);
      return;
    }
    navigate(destination);
  };

  const renderBody = () => {
    if (isLoading) {
      return Array.from({ length: 4 }).map((_, index) => (
        <Box key={index} sx={{ px: 2, py: 1.25 }}>
          <Skeleton variant="text" width="85%" height={18} />
          <Skeleton variant="text" width="45%" height={14} />
        </Box>
      ));
    }

    if (isError) {
      return (
        <Typography variant="body2" color="error" sx={{ px: 2, py: 2 }}>
          No se pudieron cargar las notificaciones.
        </Typography>
      );
    }

    if (notifications.length === 0) {
      return (
        <Box sx={{ px: 2, py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <CheckCheck size={22} color={theme.palette.text.secondary} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Estás al día. No tienes notificaciones sin leer.
          </Typography>
        </Box>
      );
    }

    return notifications.map((notification) => {
      const { text: iconColor, background: iconBackground } =
        notificationColors[notification.type] ?? notificationColors.default;
      const Icon = isContentNotification(notification)
        ? EditNote
        : NOTIFICATION_TYPES[notification.type]?.icon || Bell;
      const timestamp = isThreadNotification(notification)
        ? notification.updated_at
        : notification.created_at;

      return (
        <Box
          key={notification.id}
          role="button"
          tabIndex={0}
          onClick={() => handleNotificationClick(notification)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleNotificationClick(notification);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            py: 1,
            cursor: "pointer",
            outline: "none",
            borderBottom: `1px solid ${neutrals.gray[100]}`,
            "&:hover, &:focus-visible": { backgroundColor: theme.palette.action.hover },
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: iconBackground,
            }}
          >
            {Icon === EditNote ? (
              <Icon sx={{ color: iconColor, fontSize: 17 }} />
            ) : (
              <Icon size={15} color={iconColor} />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {getNotificationSummary(notification).trim()}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: theme.palette.text.secondary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {NOTIFICATION_TYPES[notification.type]?.label || "Notificación"}
              {timestamp && ` · ${formatRelativeDate(new Date(timestamp))}`}
            </Typography>
          </Box>

          <Tooltip title="Marcar como leído" arrow>
            <IconButton
              size="small"
              aria-label="Marcar como leído"
              onClick={(event) => {
                event.stopPropagation();
                markAsRead(notification);
              }}
              sx={{
                flexShrink: 0,
                color: iconColor,
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <CheckCheck size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    });
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Notificaciones"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          all: "unset",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          borderRadius: "50%",
          cursor: "pointer",
          transition: "background-color 150ms",
          backgroundColor: isOpen ? neutrals.gray[100] : "transparent",
          "&:hover": {
            backgroundColor: neutrals.gray[100],
          },
        }}
      >
        <Bell size={20} color={theme.palette.text.secondary} />
        {notificationsCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 5,
              right: 6,
              width: 13,
              height: 13,
              borderRadius: "50%",
              backgroundColor: colors.error.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.error.contrastText,
                fontSize: "0.65rem",
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              {notificationsCount > 9 ? "9+" : notificationsCount}
            </Typography>
          </Box>
        )}
      </Box>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 400,
              maxWidth: "calc(100vw - 24px)",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              boxShadow: theme.shadows[6],
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Notificaciones
            </Typography>
            {notificationsCount > 0 && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {notificationsCount} sin leer
              </Typography>
            )}
          </Box>
          <Button
            variant="ghost"
            size="sm"
            onClick={markAllAsRead}
            disabled={isMarkingAll || notifications.length === 0}
          >
            {isMarkingAll ? "Marcando..." : "Marcar todo como leído"}
          </Button>
        </Box>

        <Box sx={{ maxHeight: 440, overflowY: "auto" }}>
          {renderBody()}
        </Box>

        <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button variant="outline" size="sm" onClick={() => goToDashboard()} sx={{ width: "100%" }}>
            Ver todas en el dashboard
          </Button>
        </Box>
      </Popover>
    </>
  );
};
